/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { LogoFragment } from "@cosmos/hygraph-schema";
import { forwardRef } from "react";

import { css, cva, RecipeVariantProps } from "@panda/css";
import { Box } from "@panda/jsx";
import { flex } from "@panda/patterns";

import { Logo } from "./Logo.atom";

export type LogoListProps = {
	logos?: LogoFragment[] | null;
	height?: number;
	marquee?: boolean;
	gridLayout?: boolean | null;
	"aria-hidden"?: boolean | "true" | "false";
	/** When false, the logos' links stay clickable but leave the tab order. */
	focusable?: boolean;
};

const logoGrid = cva({
	base: {
		"&&": {
			display: "grid",
			columnGap: "eds.dimension.64",
			rowGap: "eds.dimension.64",
		},
	},
	variants: {
		logos: {
			1: {
				gridTemplateColumns: "repeat(1, minmax(0, 1fr))",
			},
			2: {
				gridTemplateColumns: {
					base: "repeat(2, minmax(0, 1fr))",
				},
			},
			3: {
				gridTemplateColumns: {
					base: "repeat(3, minmax(0, 1fr))",
					mdDown: "repeat(2, minmax(0, 1fr))",
				},
			},
			4: {
				gridTemplateColumns: {
					base: "repeat(4, minmax(0, 1fr))",
					xlDown: "repeat(3, minmax(0, 1fr))",
					mdDown: "repeat(2, minmax(0, 1fr))",
				},
			},
			5: {
				gridTemplateColumns: {
					base: "repeat(5, minmax(0, 1fr))",
					xlDown: "repeat(3, minmax(0, 1fr))",
					mdDown: "repeat(2, minmax(0, 1fr))",
				},
			},
		},
	},
	defaultVariants: {
		logos: 5,
	},
});

/**
 * LogoList is expected to be used within LogoMarquee, it is missing styles to be used on its own.
 * Sets the height for all of the logos.
 */
export const LogoList = forwardRef<any, LogoListProps>(
	(
		{ logos = [], height = 54, marquee = false, gridLayout = false, focusable = true, ...rest }: LogoListProps,
		forwardedRef,
	): React.JSX.Element | null => {
		if (!logos) return null;
		const logoCount = logos?.length ?? 0;

		return (
			<Box
				ref={forwardedRef}
				direction="row"
				alignItems="center"
				columnGap="eds.dimension.64"
				paddingInline="eds.dimension.32"
				rowGap="eds.dimension.64"
				flexWrap={marquee ? "nowrap" : "wrap"}
				justifyContent={marquee ? "flex-start" : "center"}
				aria-hidden={rest["aria-hidden"]}
				className={`logo-list ${css({
					// In marquee mode the parent .logo-marquee-track owns the
					// translation animation. The list itself is just a static
					// flex row of logos sized to its content.
					position: "relative",
					w: "auto",
				})}
				${gridLayout ? logoGrid({ logos: logoCount > 4 ? 5 : logoCount } as RecipeVariantProps<typeof logoGrid>) : flex()}`}
			>
				{logos.map((logo, i) => (
					<div
						key={`logo-${logo.title}-${i}`}
						className={`logo-list-wrap ${css({
							display: "inline-block",
							height: `${height}px`,
							// In marquee mode the container is `nowrap` flex with the row
							// often wider than the viewport (overflow hidden, animated by
							// translateX). Default `flex-shrink: 1` shrinks every item
							// proportionally to fit the parent width — which silently
							// squishes wide wordmarks AND square icons (e.g. 17 logos in
							// EAC: square icons rendered at 40×54 instead of their natural
							// 54×54). Locking `flex-shrink: 0` preserves intrinsic widths
							// so logos render at their authored size regardless of count.
							flexShrink: 0,
						})}`}
					>
						<Logo {...logo} height={height} tabIndex={focusable ? undefined : -1} />
					</div>
				))}
			</Box>
		);
	},
);

LogoList.displayName = "LogoList";
